import { FC, createContext, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'hooks';
import { Loader, Tabs } from 'components';
import {
  selectCurrentTeam,
  selectCurrentTeamAccountingPermissions,
  selectCurrentTeamJobPermissions,
  selectMerchantTeams,
  selectUserDetails,
  setCurrentTeam,
  showNotifier
} from 'containers/app/appSlice';
import { AssignableMember, Job, JobTimelineEvent } from '../jobs.model';
import {
  useCreateNoteMutation,
  useDeleteJobMutation,
  useDeleteNoteMutation,
  useEditExternalCompanyMutation,
  useEditJobAssigneesMutation,
  useEditJobLeadSourceMutation,
  useEditJobScheduleMutation,
  useEditJobServiceAreaMutation,
  useFindServiceAreasMutation,
  useLazyGetAssignableMembersQuery,
  useLazyGetJobByIdQuery,
  useLazyGetJobTimelineQuery,
  useLazyGetProfitabilitySummaryQuery
} from '../api';
import { useGetSuggestedAdjustmentsForJobQuery } from 'containers/accounting/pay-runs/api';
import { API_RESPONSES } from 'types/baseTypes';
import DateUtils from 'utils/dateUtils';
import { logAnalyticEvent } from 'utils/analytics';
import { JobsCleverTapEvents } from '../events';
import EditScheduleModal from '../components/EditScheduleModal';
import EditLeadSourceModal from '../components/EditLeadSourceModal';
import AssignableMembersModal from '../create-edit-job/components/AssignableMembersModal';
import JobDetailsHeader from './components/JobDetailsHeader';
import { ROOT_ROUTE_STATE } from 'routes/constants';
import EditDescriptionModal from '../components/EditDescriptionModal';
import BillingTab from './components/BillingTab';
import OverviewTab from './components/OverviewTab';
import { JobDetailsTabs } from './constants';
import RightSectionJobDetail from './components/RightSectionJobDetail';
import CreateEditNoteModal from '../components/CreateEditNoteModal';
import { DeleteNoteConfirmationModal } from '../components/DeleteNoteConfirmationModal';
import { ADDRESS_TYPE } from 'containers/customers/constants';
import { AddressField } from 'containers/invoices/invoices.model';
import AddressModal from '../components/AddressModal';
import { NotifierTypes } from 'constants/notifierConstants';
import EditExternalCompanyModal from '../components/EditExternalCompanyModal';
import HistoryTab from './components/HistoryTab';
import { ImagePreview } from 'components';
import EditStatusDetailModal from '../components/EditStatusDetailModal';
import DoneStatusSuccessModal from '../components/DoneStatusSuccessModal';
import routesPath from 'routes/RoutesPath';
import { JobStatus } from '../constant';
import { DeleteJobConfirmationModal } from '../components/DeleteJobConfrimationModal';
import EditStatusModal from '../components/EditStatusModal';
import { useGetExternalCompaniesQuery } from 'containers/settings/external-company-settings/api';
import { CommissionStatus } from 'containers/settings/commission-settings/constants';
import { useUpdateJobAddressMutation } from 'containers/dispatch/api';
import { JobDetailsSync } from './components/JobDetailsSync';
import useFSMPlanInfo from 'containers/integrations/fsm-integration/hooks/useFSMPlanInfo';
import { useLazyGetServiceAreaByIdQuery } from 'containers/settings/service-areas/api';
import { WarningIcon } from 'assets/icons';
import { Tab } from 'components/tabs/Tabs';

export interface JobPermissionsContextType {
  canEditJobs: boolean;
  canViewJobCostingsAndMargin: boolean;
  canEditJobSource: boolean;
  canViewJobSource: boolean;
}

export const JobPermissionsContext = createContext<JobPermissionsContextType>({
  canEditJobs: false,
  canViewJobCostingsAndMargin: false,
  canEditJobSource: false,
  canViewJobSource: false
});

const JobDetails: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const dispatch = useAppDispatch();
  const {
    rootJobId,
    jobId,
    rootInvoiceId,
    rootReceiptId,
    rootPage,
    rootCustomerId,
    rootDisputeId,
    rootEstimateId,
    teamId
  } = useParams();

  const searchParams = new URLSearchParams(location.search);
  const teamIdFromUrl = searchParams.get('teamId');

  const merchantTeams = useAppSelector(selectMerchantTeams);
  const currentTeam = useAppSelector(selectCurrentTeam);
  const { modifyDoneJobs, createAndEditJob, manageOthersJobs, viewJobSource, editJobSource } = useAppSelector(
    selectCurrentTeamJobPermissions
  );
  const currentUser = useAppSelector(selectUserDetails);
  const { viewJobProfitability, managePayAdjustments } = useAppSelector(selectCurrentTeamAccountingPermissions);
  const { isActiveFSMPlusPlan } = useFSMPlanInfo();

  const [selectedTab, setSelectedTab] = useState<string>(JobDetailsTabs.Overview);
  const [job, setJob] = useState<Job>();
  const [assigneeList, setAssigneeList] = useState<AssignableMember[]>([]);
  const [serviceAreas, setServiceAreas] = useState<any[]>([]);
  const [serviceAreaAssignees, setServiceAreaAssignees] = useState<AssignableMember[]>([]);
  const [isSavingAddress, setIsSavingAddress] = useState(false);
  const [contentHeight, setContentHeight] = useState<number | null>(null);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showAssigneesModal, setShowAssigneesModal] = useState(false);
  const [showLeadSourceModal, setShowLeadSourceModal] = useState(false);
  const [showExternalCompanyModal, setShowExternalCompanyModal] = useState(false);
  const [suggestionList, setSuggestionList] = useState<AssignableMember[]>([]);
  const [showDescriptionModal, setShowDescriptionModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [currentNote, setCurrentNote] = useState<JobTimelineEvent | null>(null);
  const [notesInput, setNotesInput] = useState('');
  const [showDeleteNoteConfirmation, setShowDeleteNoteConfirmation] = useState(false);
  const [showAddressModal, setShowAddressModal] = useState(false);
  const [addressToEdit, setAddressToEdit] = useState<AddressField | null>(null);
  const [addressType, setAddressType] = useState<ADDRESS_TYPE>(ADDRESS_TYPE.SERVICE);
  const [showImageViewer, setShowImageViewer] = useState(false);
  const [currentImage, setCurrentImage] = useState<number>(0);
  const [showDeleteJobConfirmation, setShowDeleteJobConfirmation] = useState(false);
  const [showEditStatusModal, setShowEditStatusModal] = useState(false);
  const [showStatusDetailModal, setShowStatusDetailModal] = useState(false);
  const [updatedStatus, setUpdatedStatus] = useState<JobStatus>(null);
  const [showStatusSuccessModal, setShowStatusSuccessModal] = useState(false);
  const [isRootState, setIsRootState] = useState(true);
  const [isRemoveLeadSourceLoading, setIsRemoveLeadSourceLoading] = useState(false);
  const [isRemoveExternalCompanyLoading, setIsRemoveExternalCompanyLoading] = useState(false);
  const tabsRef = useRef<HTMLDivElement>(null);
  const isManualServiceAreaUpdateRef = useRef(false);
  const leftContainerRef = useRef<HTMLDivElement>(null);

  const timezone = DateUtils.getTimezoneLongName();

  // API hooks
  const [editJobAssigneeMutation, editJobAssigneeState] = useEditJobAssigneesMutation();
  const { isLoading: isEditJobAssigneeLoading } = editJobAssigneeState;
  const [editJobLeadSourceMutation, editJobLeadSourceState] = useEditJobLeadSourceMutation();
  const { isLoading: isEditJobLeadSourceLoading } = editJobLeadSourceState;
  const [getJobByIdQuery, jobByIdState] = useLazyGetJobByIdQuery();
  const { data: jobData, isFetching: isJobByIdFetching } = jobByIdState;
  const [getJobTimelineQuery, getJobTimelineState] = useLazyGetJobTimelineQuery();
  const { data: jobTimelineResponse } = getJobTimelineState;
  const [getAssignableTeamMemberQuery, { data: assignableTeamMembers, isSuccess: assignableTeamMembersSuccess }] =
    useLazyGetAssignableMembersQuery();
  const [deleteNoteMutation, { isLoading: isDeleteNoteLoading, isSuccess: deleteNoteSuccess }] =
    useDeleteNoteMutation();
  const [createNoteMutation, createNoteState] = useCreateNoteMutation();
  const { isLoading: isCreateNoteLoading, isSuccess: isCreateNoteSuccess } = createNoteState;
  const [updateJobAddressMutation] = useUpdateJobAddressMutation();
  const [deleteJobMutation, { isLoading: isDeleteJobLoading }] = useDeleteJobMutation();
  const [editExternalCompanyMutation, { isLoading: isEditExternalCompanyLoading }] = useEditExternalCompanyMutation();
  const [editJobScheduleMutation, { isLoading: isEditJobScheduleLoading }] = useEditJobScheduleMutation();
  const [getProfitabilitySummaryQuery, { data: profitabilityData, isFetching: isFetchingProfitability }] =
    useLazyGetProfitabilitySummaryQuery();
  const {
    data: suggestedAdjustments,
    isFetching: isFetchingSuggestedAdjustments,
    refetch: refetchSuggestedAdjustments
  } = useGetSuggestedAdjustmentsForJobQuery(
    {
      teamId: job?.teamId,
      jobId: job?.id
    },
    {
      skip: !job?.teamId || !job?.id
    }
  );
  const [findServiceAreas] = useFindServiceAreasMutation();
  const [editJobServiceArea] = useEditJobServiceAreaMutation();
  const [getServiceAreaById] = useLazyGetServiceAreaByIdQuery();
  const { data: companiesData } = useGetExternalCompaniesQuery(
    { teamId: currentTeam?.id?.toString() || '', status: CommissionStatus.ACTIVE },
    {
      skip: !currentTeam?.id
    }
  );

  // Add a function to calculate the content height based on element heights
  const calculateContentHeight = useCallback(() => {
    const topbarElement = document.getElementById('top-bar');
    const topbarHeight = topbarElement?.offsetHeight || 64; // Default if element not found
    const headerElement = document.getElementById('job-details-header');
    const headerHeight = headerElement?.offsetHeight || 130; // Default if element not found

    // Calculate available height
    const windowHeight = window.innerHeight;
    const availableHeight = windowHeight - topbarHeight - headerHeight - 12;
    // Set a minimum height to prevent issues if elements aren't loaded yet
    const calculatedHeight = Math.max(availableHeight, 300);
    setContentHeight(calculatedHeight);
  }, []);

  // Run the calculation when elements are available and when window resizes
  useEffect(() => {
    // Initial calculation
    if (job) {
      // Wait for render to complete before measuring
      requestAnimationFrame(() => {
        calculateContentHeight();
      });
    }

    // Add resize listener
    window.addEventListener('resize', calculateContentHeight);

    // Cleanup
    return () => {
      window.removeEventListener('resize', calculateContentHeight);
    };
  }, [calculateContentHeight, job]);

  useEffect(() => {
    const teamIdVal = teamIdFromUrl ? teamIdFromUrl : teamId;
    if (teamIdVal && currentTeam?.id !== +teamIdVal) {
      const merchantTeam = merchantTeams.find(team => team.id === +teamIdVal);
      if (merchantTeam) {
        dispatch(setCurrentTeam(merchantTeam));
      } else {
        navigate(routesPath.JOBS, { replace: true });
      }
    }
  }, [location, teamIdFromUrl, teamId]);

  useEffect(() => {
    if (job) {
      const isCreatedByCurrentUser = job?.createdBy.id === currentUser?.id;
      const isAssignee = job?.assignees?.some(assignee => assignee.id === currentUser?.id);
      if (!manageOthersJobs) {
        if (!isCreatedByCurrentUser && !isAssignee) {
          navigate(routesPath.JOBS);
        }
      }
      if (!createAndEditJob) {
        if (isCreatedByCurrentUser || isAssignee) {
          navigate(routesPath.JOBS);
        }
      }
      if (job?.teamId !== currentTeam?.id) {
        navigate(routesPath.JOBS);
      }
    }
  }, [manageOthersJobs, createAndEditJob, job]);

  useEffect(() => {
    if (selectedTab === JobDetailsTabs.History) {
      refetchJobsTimeline();
    }
    if (leftContainerRef.current) {
      leftContainerRef.current.scrollTop = 0;
    }
  }, [selectedTab]);

  useEffect(() => {
    if (!isCreateNoteLoading && isCreateNoteSuccess) {
      refetchJobsTimeline();
    }
  }, [isCreateNoteSuccess, isCreateNoteLoading]);

  useEffect(() => {
    if ((jobId || rootJobId) && currentTeam?.id) {
      getJobById();
      getJobTimelineQuery({ jobId: jobId ? jobId : rootJobId, teamId: currentTeam.id, timezone });
      getAssignableTeamMemberQuery(currentTeam?.id.toString());
    }
  }, [jobId, rootJobId, currentTeam?.id]);

  useEffect(() => {
    if (jobData?.data) {
      setJob(jobData.data);
    }
  }, [jobData]);

  useEffect(() => {
    if (assignableTeamMembersSuccess) {
      let assignableMembersList = assignableTeamMembers.data.teamMemberList || [];
      if (!manageOthersJobs) {
        assignableMembersList = assignableTeamMembers.data.teamMemberList?.filter(
          member => member.merchantId === currentUser?.id
        );
      }
      setSuggestionList(assignableMembersList);
      if (job && !isJobByIdFetching) {
        const jobAssignees = job.assignees?.map(assignee => assignee.id);
        if (jobAssignees)
          setAssigneeList(
            assignableTeamMembers.data.teamMemberList.filter(member => jobAssignees.includes(member.merchantId))
          );
      }
    }
  }, [assignableTeamMembersSuccess, job, isJobByIdFetching]);

  useEffect(() => {
    if (deleteNoteSuccess) {
      setCurrentNote(null);
      setShowDeleteNoteConfirmation(false);
      refetchJobsTimeline();
    }
  }, [deleteNoteSuccess]);

  useEffect(() => {
    if (currentTeam?.id && (jobId || rootJobId)) {
      getProfitabilitySummaryQuery({
        teamId: currentTeam.id,
        jobId: jobId || rootJobId
      });
    }
  }, [currentTeam?.id, jobId, rootJobId]);

  useEffect(() => {
    if (location.state?.showExternalCompanyModal) {
      setShowExternalCompanyModal(true);
      // Clean up location state
      navigate(location.pathname, { replace: true });
    }

    if (location.state?.selectedJobTab) {
      setSelectedTab(location.state.selectedJobTab || JobDetailsTabs.OVERVIEW);
      navigate(location.pathname, { replace: true });
    }
  }, [location.state]);

  // Fetch service area assignees for AssignableMembersModal when isActiveFSMPlusPlan
  const fetchServiceAreaAssignees = useCallback(async () => {
    if (!isActiveFSMPlusPlan || !job?.jobServiceArea?.id || !currentTeam?.id) {
      setServiceAreaAssignees([]);
      return;
    }

    try {
      const data = await getServiceAreaById({
        teamId: currentTeam.id.toString(),
        serviceAreaId: job.jobServiceArea.id
      }).unwrap();

      if (data?.assignees) {
        const assignees = data.assignees.map(assignee => ({
          merchantId: assignee.id,
          name: assignee.name,
          email: assignee.email || '',
          phone: assignee.phone || '',
          profilePictureUrl: assignee.profilePictureURL || '',
          id: assignee.id,
          colorCode: ''
        }));
        let assignableMembersList = assignees;
        if (!manageOthersJobs) {
          assignableMembersList = assignees.filter(assignee => assignee.merchantId === currentUser?.id);
        }
        setServiceAreaAssignees(assignableMembersList);
      } else {
        setServiceAreaAssignees([]);
      }
    } catch (error) {
      setServiceAreaAssignees([]);
    }
  }, [isActiveFSMPlusPlan, job?.jobServiceArea?.id, currentTeam?.id, getServiceAreaById]);

  // Fetch service area assignees when job service area changes
  useEffect(() => {
    fetchServiceAreaAssignees();
  }, [fetchServiceAreaAssignees]);

  const fetchServiceAreas = useCallback(
    async (addressDetails?: AddressField) => {
      const serviceAddress = addressDetails || job?.customerLatest?.serviceAddress?.[0];
      if (
        serviceAddress &&
        'latitude' in serviceAddress &&
        'longitude' in serviceAddress &&
        serviceAddress.latitude?.toString() &&
        serviceAddress.longitude?.toString() &&
        currentTeam?.id
      ) {
        try {
          const response = await findServiceAreas({
            teamId: currentTeam.id,
            serviceAddressPoint: [serviceAddress.latitude, serviceAddress.longitude]
          }).unwrap();

          const formattedAreas = [
            ...(response.enclosingServiceAreas || []),
            ...(response.nearestServiceAreas || []),
            ...(response?.defaultServiceAreas || [])
          ];
          setServiceAreas(formattedAreas);
          return formattedAreas;
        } catch (error) {
          return [];
        }
      }
      return [];
    },
    [job?.customerLatest?.serviceAddress, currentTeam?.id, findServiceAreas]
  );

  // Refresh service areas when customer service address changes
  useEffect(() => {
    if (job?.customerLatest?.serviceAddress?.[0] && !isManualServiceAreaUpdateRef.current && isActiveFSMPlusPlan) {
      fetchServiceAreas();
    }
  }, [job?.customerLatest?.serviceAddress, fetchServiceAreas]);

  const JobDetailsTabsList = useMemo<Tab[]>(
    () => [
      { id: JobDetailsTabs.OVERVIEW, title: JobDetailsTabs.OVERVIEW },
      {
        id: JobDetailsTabs.BILLING,
        title: JobDetailsTabs.BILLING,
        icon:
          (suggestedAdjustments?.totalCount || 0) > 0 && managePayAdjustments ? (
            <WarningIcon className="path-stroke-current text-pending-orange" />
          ) : null,
        iconPosition: 'right'
      },
      { id: JobDetailsTabs.HISTORY, title: JobDetailsTabs.HISTORY }
    ],
    [suggestedAdjustments?.totalCount, managePayAdjustments]
  );

  const autoUpdateServiceAreaAndAssignees = useCallback(
    async (addressDetails: AddressField) => {
      if (!currentTeam?.id || !(jobId || rootJobId) || !isActiveFSMPlusPlan) return;

      try {
        // Set flag to indicate manual service area update is in progress
        isManualServiceAreaUpdateRef.current = true;
        const formattedAreas = await fetchServiceAreas(addressDetails);

        if (formattedAreas?.length > 0) {
          // Get the first service area
          const firstServiceArea = formattedAreas[0];

          const data = await getServiceAreaById({
            teamId: currentTeam.id.toString(),
            serviceAreaId: firstServiceArea?.serviceAreaId
          }).unwrap();

          let assigneesToUpdate: string[] = [];

          if (data?.assignees && data.assignees?.length > 0) {
            // Use the first assignee
            assigneesToUpdate = [data.assignees[0]?.id];
          }

          // Update both service area and assignees
          await editJobAssigneeMutation({
            teamId: currentTeam.id,
            jobId: jobId || rootJobId,
            assignees: assigneesToUpdate?.length > 0 ? assigneesToUpdate : []
          }).unwrap();

          await editJobServiceArea({
            teamId: currentTeam.id,
            jobId: jobId || rootJobId,
            serviceAreaId: firstServiceArea.serviceAreaId
          }).unwrap();
        } else {
          // No service areas available, clear both service area and assignees
          await editJobAssigneeMutation({
            teamId: currentTeam.id,
            jobId: jobId || rootJobId,
            assignees: []
          }).unwrap();

          await editJobServiceArea({
            teamId: currentTeam.id,
            jobId: jobId || rootJobId,
            serviceAreaId: ' '
          }).unwrap();
        }
      } catch (error) {
        // Error handling is already handled by the API mutations
      } finally {
        // Reset flag after operation completes (success or error)
        isManualServiceAreaUpdateRef.current = false;
      }
    },
    [
      currentTeam?.id,
      jobId,
      rootJobId,
      isActiveFSMPlusPlan,
      fetchServiceAreas,
      getServiceAreaById,
      editJobAssigneeMutation,
      editJobServiceArea
    ]
  );

  const canEditJobs = () => {
    if (job?.status === JobStatus.DONE) {
      return modifyDoneJobs && job?.isEditable;
    }
    return createAndEditJob && job?.isEditable;
  };

  const handleCustomerClick = () => {
    const rootState = {
      ...ROOT_ROUTE_STATE,
      isFromJob: true
    };

    if (rootJobId) {
      navigate(`/jobs/${currentTeam?.id}/${rootJobId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam?.id}/${rootInvoiceId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootPage) {
      navigate(`/payouts/${rootPage}/${currentTeam?.id}/${rootPage}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam?.id}/${rootReceiptId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam?.id}/${rootCustomerId}/customer`, {
        state: rootState
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam?.id}/${rootDisputeId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam?.id}/${rootEstimateId}/customer/${job.customerCurrent.id}`, {
        state: rootState
      });
    }
  };

  const formatMemberList = (memberList: AssignableMember[]) => {
    return memberList.map(member => ({
      id: member.merchantId,
      name: member.name,
      profilePictureURL: member.profilePictureUrl
    }));
  };

  const handleAssigneesUpdate = (membersList: AssignableMember[]) => {
    let assigneesToUpdate: AssignableMember[] = membersList;
    if (!manageOthersJobs) {
      const isCurrentUserInMembersList = membersList.some(member => member.merchantId === currentUser?.id);
      if (isCurrentUserInMembersList) {
        // If assigneeList is empty and current user is being added, use membersList
        // Otherwise, preserve the existing assigneeList
        assigneesToUpdate = assigneeList.length > 0 ? assigneeList : membersList;
      } else {
        assigneesToUpdate = assigneeList.filter(assignee => assignee.merchantId !== currentUser?.id);
      }
    }
    editJobAssigneeMutation({
      teamId: currentTeam.id,
      jobId: jobId || rootJobId,
      assignees: assigneesToUpdate.map(assignee => assignee.merchantId)
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setJob(prev => ({ ...prev, assignees: formatMemberList(assigneesToUpdate) }));
        setAssigneeList(assigneesToUpdate);
        setShowAssigneesModal(false);
      }
    });
  };

  const handleMemberSelectionChange = (member: AssignableMember, isSelected: boolean) => {
    // Show toast when user without manageOthersJobs permission unchecks themselves
    if (
      !manageOthersJobs &&
      member.merchantId === currentUser?.id &&
      !isSelected // User is being unchecked
    ) {
      const message = `
          This action will unassign you, as you do not have permission to modify other assignees.
          <br />
          Please contact the owner or admin for more details.
        `;
      dispatch(
        showNotifier({
          message: {
            primaryMessage: message,
            isMessageHtml: true
          },
          type: NotifierTypes.WARNING,
          showClose: true,
          duration: 8000
        })
      );
    }
  };

  const handleLeadSourceUpdate = (leadSourceType: string, leadSource: string) => {
    return editJobLeadSourceMutation({
      leadSourceType,
      leadSource,
      jobId: rootJobId || jobId,
      teamId: currentTeam.id
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setJob(prev => ({ ...prev, leadSource, leadSourceType }));
        logAnalyticEvent(JobsCleverTapEvents.webJobsLeadSourcePageView);
        setShowLeadSourceModal(false);
      }
    });
  };

  const handleRemoveLeadSource = () => {
    setIsRemoveLeadSourceLoading(true);
    return editJobLeadSourceMutation({
      leadSourceType: null,
      leadSource: null,
      jobId: rootJobId || jobId,
      teamId: currentTeam.id
    })
      .then(response => {
        if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
          setJob(prev => ({ ...prev, leadSource: '', leadSourceType: null }));
          logAnalyticEvent(JobsCleverTapEvents.webJobsLeadSourcePageView);
          setShowLeadSourceModal(false);
        }
      })
      .finally(() => {
        setIsRemoveLeadSourceLoading(false);
      });
  };

  const handleScheduleUpdate = (scheduledFrom: string, scheduledTo: string) => {
    editJobScheduleMutation({
      scheduledFrom,
      scheduledTo,
      jobId: job.id,
      teamId: currentTeam.id
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setJob(prev => ({ ...prev, scheduledFrom, scheduledTo }));
        getJobById();
        setShowScheduleModal(false);
      }
    });
  };

  // modal handlers
  const handleEditScheduleClick = () => {
    setShowScheduleModal(true);
    logAnalyticEvent(JobsCleverTapEvents.webJobsSchedulePageView);
  };

  const handleEditAssigneesClick = () => {
    setShowAssigneesModal(true);
  };

  const filteredAssigneeList = useMemo(() => {
    const isServiceAreaContext = isActiveFSMPlusPlan && serviceAreas?.length && job?.jobServiceArea?.id;
    if (isServiceAreaContext) {
      // Filter assigneeList to only include members available in serviceAreaAssignees
      return assigneeList.filter(
        assignee => serviceAreaAssignees.some(serviceAssignee => serviceAssignee.merchantId === assignee.merchantId)
        // eslint-disable-next-line function-paren-newline
      );
    }
    // For non-service area context, return original list
    return assigneeList;
  }, [isActiveFSMPlusPlan, serviceAreas, job?.jobServiceArea?.id, assigneeList, serviceAreaAssignees]);

  const handleEditLeadSourceClick = () => {
    setShowLeadSourceModal(true);
    logAnalyticEvent(JobsCleverTapEvents.webJobsLeadSourcePageView);
  };

  // handler for description update
  const handleDescriptionUpdate = (description: string) => {
    setJob(prev => ({ ...prev, description }));
    setShowDescriptionModal(false);
  };

  // handler for edit description click
  const handleEditDescriptionClick = () => {
    setShowDescriptionModal(true);
  };

  // function to handle adding notes
  const handleAddNoteClick = () => {
    setCurrentNote(null);
    setShowNotesModal(true);
  };

  const handleEditNote = (event: JobTimelineEvent) => {
    setCurrentNote(event);
    setShowNotesModal(true);
  };

  const handleDeleteNote = (event: JobTimelineEvent) => {
    setCurrentNote(event);
    setShowDeleteNoteConfirmation(true);
  };

  // function to refresh the job timeline
  const refetchJobsTimeline = () => {
    getJobTimelineQuery({
      jobId: jobId ? jobId : rootJobId,
      teamId: currentTeam.id,
      timezone
    });
  };

  // function to handle closing the notes modal
  const handleCloseNotesModalClick = () => {
    setCurrentNote(null);
    setShowNotesModal(false);
  };

  const getJobById = () => {
    getJobByIdQuery({ jobId: jobId ? jobId : rootJobId, teamId: currentTeam.id, isFromEdit: true });
  };

  // handler for edit address click
  const handleEditAddress = (address: AddressField, type: ADDRESS_TYPE) => {
    setAddressToEdit(address);
    setAddressType(type);
    setShowAddressModal(true);
  };

  const handleSidePanelClose = () => {
    navigate(`/jobs/${currentTeam?.id}/${jobId || rootJobId}`, { replace: true });
  };

  // handler for address update
  const handleAddressUpdate = async addressDetails => {
    if (!addressToEdit || !currentTeam || !job) return;
    setIsSavingAddress(true);
    const addressPayload = {
      ...addressDetails,
      teamId: currentTeam.id.toString(),
      customerId: job.customerCurrent.id.toString(),
      isBillingSameAsService: false,
      jobId: jobId || rootJobId,
      type: ADDRESS_TYPE.SERVICE,
      addressId: addressDetails.id
    };

    try {
      const response = await updateJobAddressMutation(addressPayload);

      if ('data' in response) {
        // Trigger auto-update for service area and assignees if FieldServe Plus
        if (isActiveFSMPlusPlan) {
          await autoUpdateServiceAreaAndAssignees(addressDetails);
        }

        dispatch(
          showNotifier({
            message: {
              primaryMessage: 'Address updated successfully',
              isMessageHtml: false
            },
            type: NotifierTypes.SUCCESS,
            showClose: true,
            fontStyle: 'text-primary font-normal'
          })
        );
        getJobById();
      }
    } catch (error) {
      //
    } finally {
      setAddressToEdit(null);
      setShowAddressModal(false);
      setIsSavingAddress(false);
    }
  };

  const handleExternalCompanyUpdate = (companyId: string) => {
    return editExternalCompanyMutation({
      teamId: currentTeam.id.toString(),
      jobId: jobId || rootJobId,
      externalCompanyId: companyId
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setJob(prev => ({ ...prev, externalCompanyId: companyId }));
        setShowExternalCompanyModal(false);
      }
    });
  };

  const handleRemoveExternalCompany = () => {
    setIsRemoveExternalCompanyLoading(true);
    return editExternalCompanyMutation({
      teamId: currentTeam.id.toString(),
      jobId: jobId || rootJobId,
      externalCompanyId: null
    })
      .then(response => {
        if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
          setJob(prev => ({ ...prev, externalCompanyId: null }));
          setShowExternalCompanyModal(false);
        }
      })
      .finally(() => {
        setIsRemoveExternalCompanyLoading(false);
      });
  };

  const showPreview = (index: number, note: JobTimelineEvent) => {
    setCurrentNote(note);
    setCurrentImage(index);
    setShowImageViewer(true);
    logAnalyticEvent(JobsCleverTapEvents.webImageViewerOpened, {
      source: 'job-details-page'
    });
  };

  const closePreview = () => {
    setCurrentNote(null);
    setCurrentImage(0);
    setShowImageViewer(false);
  };

  // handler for creating notes
  const handleCreateNote = (note: string, clearInput: () => void) => {
    if (!note) return;
    createNoteMutation({
      comment: note,
      attachments: [],
      jobId: jobId || rootJobId,
      teamId: currentTeam.id
    }).then(() => {
      clearInput(); // Clear the specific input that triggered the note creation
    });
  };

  const handleInvoiceClick = (invoiceId: string) => {
    if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/invoice/${invoiceId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/invoice/${invoiceId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/invoice/${invoiceId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/invoice/${invoiceId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/invoice/${invoiceId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/invoice/${invoiceId}`, {
        state: ROOT_ROUTE_STATE
      });
    }
  };

  const handleEstimateClick = (estimateId: string) => {
    if (rootJobId) {
      navigate(`/jobs/${currentTeam.id}/${rootJobId}/estimate/${estimateId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootCustomerId) {
      navigate(`/customers/${currentTeam.id}/${rootCustomerId}/estimate/${estimateId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootInvoiceId) {
      navigate(`/invoices/${currentTeam.id}/${rootInvoiceId}/estimate/${estimateId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootDisputeId) {
      navigate(`/payments/disputes/${currentTeam.id}/${rootDisputeId}/estimate/${estimateId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootReceiptId) {
      navigate(`/payments/receipts/${currentTeam.id}/${rootReceiptId}/estimate/${estimateId}`, {
        state: ROOT_ROUTE_STATE
      });
    } else if (rootEstimateId) {
      navigate(`/estimates/${currentTeam.id}/${rootEstimateId}/estimate/${estimateId}`, {
        state: ROOT_ROUTE_STATE
      });
    }
  };

  const handleStatusChange = (status?: JobStatus) => {
    if (status) {
      setUpdatedStatus(status);
      setShowEditStatusModal(false);
      setShowStatusDetailModal(true);
      setIsRootState(true);
    } else {
      setShowEditStatusModal(true);
      setIsRootState(false);
    }
  };

  const handleStatusSave = (status: JobStatus) => {
    if (refetchJobsTimeline) {
      refetchJobsTimeline();
    }
    setJob(prev => ({ ...prev, status }));
    setShowStatusDetailModal(false);

    // Show success modal only for Done status
    if (status === JobStatus.DONE) {
      setShowStatusSuccessModal(true);
    }
  };

  const handleCloseStatusModal = () => {
    setShowEditStatusModal(false);
    setShowStatusDetailModal(false);
    setUpdatedStatus(null);
  };

  const handleEditStatusDetailBackClick = () => {
    setShowEditStatusModal(true);
    setShowStatusDetailModal(false);
  };

  const handleAddOrViewInvoice = () => {
    if (job.invoices?.length > 0) {
      handleInvoiceClick(job.invoices[0].id);
    } else {
      navigate(`/invoices/create?jobId=${job.id}`);
    }
    setShowStatusSuccessModal(false);
  };

  const handleDeleteJob = () => {
    setShowDeleteJobConfirmation(true);
  };

  const onDeleteJob = () => {
    deleteJobMutation({ jobId: job.id, teamId: currentTeam.id });
    logAnalyticEvent(JobsCleverTapEvents.webDeleteJobConfirmation);
    navigate(routesPath.JOBS);
  };

  const handleViewBillingDetailsClick = () => {
    setSelectedTab(JobDetailsTabs.Billing);
  };

  const handleRemoveDate = () => {
    editJobScheduleMutation({
      jobId: job.id,
      teamId: currentTeam.id,
      scheduledFrom: null,
      scheduledTo: null
    }).then(response => {
      if ('data' in response && response.data.data.message === API_RESPONSES.SUCCESS) {
        setJob(prev => ({
          ...prev,
          scheduledFrom: null,
          scheduledTo: null
        }));
        getJobById();
        setShowScheduleModal(false);
      }
    });
  };

  if (!job) {
    return null;
  }

  return (
    <div className="flex h-full flex-col" id="job-details-content">
      {isJobByIdFetching ? (
        <Loader />
      ) : (
        <>
          <JobPermissionsContext.Provider
            value={{
              canEditJobs: canEditJobs(),
              canViewJobCostingsAndMargin: viewJobProfitability,
              canEditJobSource: editJobSource,
              canViewJobSource: viewJobSource
            }}>
            <div id="job-details-header" className="flex flex-col">
              {/* Header */}
              <JobDetailsHeader job={job} handleStatusChange={handleStatusChange} handleDeleteJob={handleDeleteJob} />
            </div>

            <div className="flex grow flex-col gap-2 bg-secondaryBtn pt-2 sm:flex-row">
              <div
                id="left-container"
                className="customNormalScroll flex max-w-[820px] grow
            flex-col overflow-y-auto rounded-tr-md bg-primary lg:max-w-none"
                ref={leftContainerRef}
                style={{ height: contentHeight ? `${contentHeight}px` : 'calc(100vh - 230px)' }}>
                {/* Tabs */}
                <div
                  id="job-details-tabs"
                  ref={tabsRef}
                  className="sticky top-0 z-[5] border-b border-borderGray bg-primary">
                  <Tabs
                    tabs={JobDetailsTabsList}
                    selectedTab={selectedTab}
                    onTabSelect={setSelectedTab}
                    tabStyle="px-4 !py-4 w-[158px] text-17px font-bold"
                  />
                </div>
                <>
                  {selectedTab === JobDetailsTabs.Overview && (
                    <OverviewTab
                      job={job}
                      onCustomerClick={handleCustomerClick}
                      onEditScheduleClick={handleEditScheduleClick}
                      onEditAssigneesClick={handleEditAssigneesClick}
                      onEditLeadSourceClick={handleEditLeadSourceClick}
                      onEditExternalCompanyClick={() => setShowExternalCompanyModal(true)}
                      onEditDescriptionClick={handleEditDescriptionClick}
                      onEditAddress={handleEditAddress}
                      isSingleMemberTeam={false}
                      onViewBillingDetails={handleViewBillingDetailsClick}
                      profitabilityData={profitabilityData?.data}
                      isLoading={isFetchingProfitability}
                      serviceAreas={serviceAreas}
                      onRefreshServiceAreas={fetchServiceAreas}
                      refetchJob={getJobById}
                    />
                  )}

                  {selectedTab === JobDetailsTabs.Billing && (
                    <BillingTab
                      job={job}
                      onInvoiceClick={handleInvoiceClick}
                      onEstimateClick={handleEstimateClick}
                      profitabilityData={profitabilityData?.data}
                      suggestedAdjustments={suggestedAdjustments}
                      isFetchingSuggestedAdjustments={isFetchingSuggestedAdjustments}
                      refetchSuggestedAdjustments={refetchSuggestedAdjustments}
                    />
                  )}

                  {selectedTab === JobDetailsTabs.History && (
                    <HistoryTab
                      jobTimeline={jobTimelineResponse?.data?.timelineList}
                      onEditNote={handleEditNote}
                      onAddNote={handleAddNoteClick}
                      onDeleteNote={handleDeleteNote}
                      showPreview={showPreview}
                      onCreateNote={handleCreateNote}
                    />
                  )}
                </>
              </div>

              <div
                id="right-section"
                className="customNormalScroll relative shrink-0 overflow-y-auto"
                style={{ height: contentHeight ? `${contentHeight}px` : 'calc(100vh - 230px)' }}>
                <RightSectionJobDetail
                  job={job}
                  jobTimeline={jobTimelineResponse?.data?.timelineList}
                  onEditScheduleClick={handleEditScheduleClick}
                  onEditAssigneesClick={handleEditAssigneesClick}
                  onEditNoteClick={handleEditNote}
                  onDeleteNoteClick={handleDeleteNote}
                  onAddNoteClick={handleAddNoteClick}
                  getJobTimeline={refetchJobsTimeline}
                  refetchJobDetails={getJobById}
                  onCreateNote={handleCreateNote}
                  onInvoiceClick={handleInvoiceClick}
                  onEstimateClick={handleEstimateClick}
                  onCustomerClick={handleCustomerClick}
                  currentJobTab={selectedTab as JobDetailsTabs}
                />
              </div>
            </div>
          </JobPermissionsContext.Provider>
        </>
      )}

      {/* Modals */}
      {showScheduleModal && (
        <EditScheduleModal
          onClose={() => setShowScheduleModal(false)}
          onSave={handleScheduleUpdate}
          onRemove={handleRemoveDate}
          scheduledFrom={job.scheduledFrom}
          scheduledTo={job.scheduledTo}
          isScheduled={job.isScheduled}
          isLoading={isEditJobScheduleLoading}
          showRemoveOption={true}
        />
      )}

      {showAssigneesModal && (
        <AssignableMembersModal
          list={
            isActiveFSMPlusPlan && serviceAreas?.length && job?.jobServiceArea?.id
              ? serviceAreaAssignees
              : suggestionList
          }
          onClose={() => setShowAssigneesModal(false)}
          selectedMembers={filteredAssigneeList}
          onSave={handleAssigneesUpdate}
          isLoading={isEditJobAssigneeLoading}
          onMemberSelectionChange={handleMemberSelectionChange}
        />
      )}

      {showLeadSourceModal && (
        <EditLeadSourceModal
          open={showLeadSourceModal}
          onClose={() => setShowLeadSourceModal(false)}
          leadSource={job.leadSource}
          leadSourceType={job.leadSourceType}
          onSaveClick={handleLeadSourceUpdate}
          jobNumber={job.jobNumber}
          isActionInProgress={isEditJobLeadSourceLoading}
          onRemoveClick={handleRemoveLeadSource}
          isRemoveLoading={isRemoveLeadSourceLoading}
        />
      )}

      {showDescriptionModal && (
        <EditDescriptionModal
          description={job.description}
          onClose={() => setShowDescriptionModal(false)}
          onSave={handleDescriptionUpdate}
          jobId={job.id}
          teamId={currentTeam.id}
          jobNumber={job.jobNumber}
        />
      )}

      {showNotesModal && (
        <CreateEditNoteModal
          onClose={handleCloseNotesModalClick}
          isEdit={!!currentNote}
          currentNote={currentNote}
          teamId={currentTeam.id}
          jobId={rootJobId || jobId}
          getJobTimeline={refetchJobsTimeline}
          defaultNote={notesInput}
          setNotesInput={setNotesInput}
        />
      )}

      {showDeleteNoteConfirmation && (
        <DeleteNoteConfirmationModal
          handleDelete={() => {
            deleteNoteMutation({
              teamId: currentTeam.id,
              jobId: rootJobId || jobId,
              noteId: currentNote?.noteId
            });
          }}
          onCloseClick={() => {
            setCurrentNote(null);
            setShowDeleteNoteConfirmation(false);
          }}
          processingDelete={isDeleteNoteLoading}
        />
      )}
      {showAddressModal && addressToEdit && (
        <AddressModal
          onClose={() => setShowAddressModal(false)}
          type={addressType}
          onAddAddress={handleAddressUpdate}
          isEdit={true}
          initialAddress={addressToEdit}
          isLoading={isSavingAddress}
        />
      )}

      {showExternalCompanyModal && (
        <EditExternalCompanyModal
          open={showExternalCompanyModal}
          onClose={() => setShowExternalCompanyModal(false)}
          jobNumber={job.jobNumber}
          selectedCompanyId={job?.externalCompany?.id}
          onSaveClick={handleExternalCompanyUpdate}
          isActionInProgress={isEditExternalCompanyLoading}
          companies={companiesData?.data || []}
          onRemoveClick={handleRemoveExternalCompany}
          isRemoveLoading={isRemoveExternalCompanyLoading}
        />
      )}

      {showImageViewer && (
        <ImagePreview onClose={closePreview} images={currentNote?.attachments} selectedIndex={currentImage} />
      )}

      {showEditStatusModal && (
        <EditStatusModal
          status={job.status}
          onBackClick={handleCloseStatusModal}
          handleStatusChange={handleStatusChange}
          jobNumber={job.jobNumber}
        />
      )}

      {showStatusDetailModal && (
        <EditStatusDetailModal
          status={updatedStatus}
          onBackClick={handleEditStatusDetailBackClick}
          onClose={handleCloseStatusModal}
          onSave={handleStatusSave}
          jobId={rootJobId || jobId}
          teamId={currentTeam.id}
          jobNumber={job.jobNumber}
          isRootState={isRootState}
        />
      )}

      {showStatusSuccessModal && (
        <DoneStatusSuccessModal
          onAddInvoiceClick={handleAddOrViewInvoice}
          invoice={job?.invoices?.length > 0 ? job.invoices[0] : null}
          onClose={() => setShowStatusSuccessModal(false)}
        />
      )}

      {showDeleteJobConfirmation && (
        <DeleteJobConfirmationModal
          onCloseClick={() => setShowDeleteJobConfirmation(false)}
          handleDelete={onDeleteJob}
          processingDelete={isDeleteJobLoading}
        />
      )}

      <Outlet context={{ handleSidePanelClose, refetchJobDetails: getJobById }} />
      <JobDetailsSync jobId={job.id} onJobNoteUpdated={refetchJobsTimeline} onJobReload={getJobById} />
    </div>
  );
};

export default JobDetails;
